import React from "react"

import type { OneVerifyDemoPolicyId } from "@common-shared/config/oneVerify/oneVerifyDemo.config"
import type { IOneVerifyDemoPolicyDefinition } from "@common-shared/types/oneVerifyDemo.types"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { DemoHelper } from "../../../helpers/demo.helper"

const mapPolicyToSelectOption = (
    policy: IOneVerifyDemoPolicyDefinition,
): ISelectRenderedOption => ({
    displayValue: policy.label,
    dropdownDisplayValue: policy.label,
    dropdownDisplayValueAdditional: policy.shortDescription,
    value: policy.id,
})

export const DemoPolicyPickerComponent: React.FunctionComponent<
    IDemoPolicyPickerComponentProps
> = props => {
    const { policies, selectedPolicy } = props
    const policiesKey = policies.map(policy => policy.id).join(",")

    const showClearButton = !!props.allowClear && !!selectedPolicy

    return (
        <div className={`w-full ${props.className || ""}`}>
            <PrimaryText weight="medium" className="mb-[8px]">
                Demo policy
            </PrimaryText>

            <div className="flex items-center gap-[12px]">
                <SelectElement
                    key={`${selectedPolicy?.id ?? "none"}-${policiesKey}`}
                    className="flex-1 min-w-0"
                    name="demoPolicyPicker"
                    placeholder="Select a demo policy"
                    initialFetch
                    dataFetcher={(search?: string) =>
                        DemoHelper.filterPolicies(policies, search)
                    }
                    itemRenderer={mapPolicyToSelectOption}
                    // SelectElement matches its options by display value, so the
                    // selected option stays highlighted in the dropdown.
                    defaultValue={
                        selectedPolicy
                            ? mapPolicyToSelectOption(selectedPolicy)
                                  .displayValue
                            : undefined
                    }
                    onSelected={(policyId: OneVerifyDemoPolicyId) => {
                        if (policyId) {
                            props.onPolicySelected(policyId)
                        }
                    }}
                />

                {showClearButton ? (
                    <ButtonElement
                        label="Clear"
                        type="default"
                        size="large"
                        htmlType="button"
                        onClick={() => props.onPolicyCleared?.()}
                    />
                ) : null}
            </div>

            {selectedPolicy ? (
                <PrimaryText className="mt-[8px] text-gray-600">
                    {selectedPolicy.longDescription}
                </PrimaryText>
            ) : null}
        </div>
    )
}

interface IDemoPolicyPickerComponentProps {
    className?: string
    policies: IOneVerifyDemoPolicyDefinition[]
    selectedPolicy?: IOneVerifyDemoPolicyDefinition
    allowClear?: boolean
    onPolicySelected: (policyId: OneVerifyDemoPolicyId) => void
    onPolicyCleared?: () => void
}
